import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Play, Pause, RotateCcw, RotateCw, Minus, X, Link2, Check, ListMusic, Rss, Plus } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { useRadio, fmtTime } from "./RadioContext";
import { resolveMoment } from "./MomentLink";
import { STARFLOWER_LOGO } from "../components/StarPathMark";
import { DraggableTile, tileDefault } from "../hooks/useDragPosition";

export const CYAN = "#54E3FF";

// Launcher tile: starflower in a cyan ring. Lives on the home banner. Opens the music player (Dolly).
export const RadioPill = ({ className = "", size = 72, testId = "radio-pill", style }) => {
  return (
    <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("wall:play-music"))} style={{ ...(size ? { width: size, height: size } : {}), ...style }}
      className={`${/\babsolute\b|\bfixed\b/.test(className) ? "" : "relative"} rounded-full overflow-hidden ring-4 ring-[#54E3FF] shadow-2xl bg-[#120A24] hover:scale-105 transition-transform ${className}`}
      aria-label="Music — open the player" title="This music saves lives — tap to play" data-testid={testId}>
      <img src={STARFLOWER_LOGO} alt="Starflower of Destiny" className="h-full w-full object-cover" />
      <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 bg-[#120A24]/85 text-[#54E3FF] text-[length:var(--onair,9px)] font-black tracking-[0.2em] text-center py-0.5 [-webkit-text-stroke:0.5px_#54E3FF] [text-shadow:0_0_6px_rgba(84,227,255,.85)]" data-testid="radio-pill-on-air">ON AIR</span>
    </button>
  );
};

export const momentUrl = (slug, t) => `${window.location.origin}/news/${slug}?t=${Math.floor(t)}`;

export const ChapterList = ({ episode, onPick, current = -1, compact = false, testId = "chapters" }) => {
  const copy = async (e, c) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(momentUrl(episode.slug, c.t)); toast.success(`Link copied — "${c.title}"`); } catch { toast(momentUrl(episode.slug, c.t)); }
  };
  return (
    <ol className={`list-none p-0 m-0 ${compact ? "space-y-0.5" : "space-y-1"}`} data-testid={testId}>
      {(episode?.chapters || []).map((c, i) => (
        <li key={`${c.t}-${i}`} className="group/ch flex items-start gap-1">
          <button type="button" onClick={() => onPick(c.t)} data-testid={`${testId}-${i}`}
            className={`flex-1 text-left flex items-start gap-3 rounded-lg px-2 py-1.5 transition-colors ${i === current ? "bg-[#54E3FF]/15 text-white" : "text-white/80 hover:bg-white/10"}`}>
            <span className={`font-mono text-xs tabular-nums shrink-0 mt-0.5 w-12 text-right ${i === current ? "text-[#54E3FF]" : "text-white/50"}`}>{fmtTime(c.t)}</span>
            <span className={`${compact ? "text-xs" : "text-sm"} leading-snug`}>{c.title}</span>
          </button>
          <button type="button" onClick={(e) => copy(e, c)} className="shrink-0 mt-1.5 p-1 rounded text-white/30 hover:text-[#54E3FF] opacity-60 sm:opacity-0 sm:group-hover/ch:opacity-100 focus:opacity-100 transition-opacity" aria-label={`Copy link to ${c.title}`} title="Copy link to this chapter" data-testid={`${testId}-link-${i}`}>
            <Link2 className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ol>
  );
};

// Admin-only: mark a new chapter at the current moment, right from the player.
const MarkMoment = ({ episode, time }) => {
  const { user } = useAuth();
  const r = useRadio();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  if (user?.role !== "admin") return null;
  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      const res = await axios.post(`${API}/admin/radio/episodes/${episode.episode_id}/chapters`, { t: Math.floor(time), title: title.trim() }, { withCredentials: true });
      r.replaceEpisode(res.data); setTitle(""); setEditing(false); toast.success(`Chapter added at ${fmtTime(time)}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't add chapter"); }
  };
  if (!editing) return <button type="button" onClick={() => setEditing(true)} className="rounded-full border border-dashed border-[#54E3FF]/50 text-[#54E3FF] px-2.5 py-1 text-[11px] font-bold hover:bg-[#54E3FF]/10 inline-flex items-center gap-1" data-testid="radio-dock-mark-moment"><Plus className="h-3 w-3" /> chapter @ {fmtTime(time)}</button>;
  return (
    <form onSubmit={save} className="flex items-center gap-1 w-full" data-testid="radio-dock-mark-form">
      <span className="font-mono text-[11px] text-[#54E3FF] shrink-0">{fmtTime(time)}</span>
      <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Chapter title" className="flex-1 min-w-0 rounded-md bg-white/10 px-2 py-1 text-xs text-white placeholder:text-white/40 outline-none focus:ring-1 focus:ring-[#54E3FF]" data-testid="radio-dock-mark-title" />
      <button type="submit" className="rounded-full bg-[#54E3FF] text-[#120A24] px-2.5 py-1 text-[11px] font-black" data-testid="radio-dock-mark-save">Add</button>
      <button type="button" onClick={() => setEditing(false)} className="text-white/50 text-[11px] px-1">✕</button>
    </form>
  );
};

const Ticks = ({ episode, duration, onSeek }) => (
  <>
    {duration > 0 && (episode?.chapters || []).map((c, i) => (
      <button key={i} type="button" onClick={(e) => { e.stopPropagation(); onSeek(c.t); }} title={c.title}
        className="absolute top-0 h-full w-[3px] -translate-x-1/2 bg-[#120A24]/70 hover:bg-white" style={{ left: `${(c.t / duration) * 100}%` }} aria-label={`Jump to ${c.title}`} />
    ))}
  </>
);

// Any in-app link to /listen/<key> or /news/<slug>?t=<sec> plays in the dock instead of leaving the page
// (Cmd/Ctrl-click still navigates). Lets pasted moment links work anywhere — guides, page-builder text, footers.
const useMomentLinks = () => {
  const r = useRadio();
  useEffect(() => {
    const onClick = async (e) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.button !== 0) return;
      const a = e.target.closest?.("a[href]"); if (!a) return;
      let url; try { url = new URL(a.getAttribute("href"), window.location.origin); } catch { return; }
      if (url.origin !== window.location.origin) return;
      const listen = url.pathname.match(/^\/listen\/([a-z0-9-]+)$/i);
      const news = url.pathname.match(/^\/news\/([a-z0-9-]+)$/i);
      const t = parseInt(url.searchParams.get("t") || "", 10);
      if (!listen && !(news && t >= 0 && url.searchParams.has("t"))) return;
      e.preventDefault();
      try {
        if (listen) { const m = (await axios.get(`${API}/radio/moments/${listen[1]}`)).data; r.load(m.episode, m.t); }
        else { const ep = (await axios.get(`${API}/radio/episodes/${news[1]}`)).data; r.load(ep, t); }
      } catch { window.location.assign(url.pathname + url.search); }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [r]);
};

export const RadioDock = () => {
  const r = useRadio();
  useMomentLinks();
  // Pages that must not import radio/* (import-cycle risk, e.g. the home map) ask for a Hear-it moment via a window event.
  useEffect(() => {
    const onPlay = (e) => {
      const key = e.detail?.key; if (!key) return;
      // Cue and play, but keep the dock minimized so it doesn't cover what the visitor is looking at (e.g. the Night map).
      resolveMoment(key).then((m) => { if (m) { r.load(m.episode, m.t); if (e.detail?.minimized) setTimeout(() => r.setMinimized(true), 50); } });
    };
    window.addEventListener("wall:play-moment", onPlay);
    return () => window.removeEventListener("wall:play-moment", onPlay);
  }, [r]);
  const [copied, setCopied] = useState(false);
  const [isMobile] = useState(() => window.innerWidth < 640);
  if (!r?.open || !r.episode) return null;
  const { episode: ep, playing, time, duration, rate, chapterIndex } = r;
  if (r.minimized) {
    return (
      <DraggableTile posKey="wall_radio_tile_pos" defaultPos={tileDefault("right")} onTap={() => r.setMinimized(false)} ariaLabel="Expand Range Free Radio" testId="radio-minimized-tile"
        className="h-[72px] w-[72px] rounded-full overflow-hidden ring-4 ring-[#54E3FF] shadow-2xl bg-[#120A24]">
        <img src={STARFLOWER_LOGO} alt="Starflower of Destiny — Range Free Radio" className="h-full w-full object-cover pointer-events-none" draggable={false} />
        <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 bg-[#120A24]/85 text-[#54E3FF] text-[9px] font-black tracking-[0.2em] text-center py-0.5 pointer-events-none [-webkit-text-stroke:0.5px_#54E3FF] [text-shadow:0_0_6px_rgba(84,227,255,.85)]">ON AIR</span>
        {playing && <span className="absolute top-1 right-1 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-[#120A24] animate-pulse" />}
      </DraggableTile>
    );
  }

  const copyMoment = async () => {
    const url = `${window.location.origin}/news/${ep.slug}?t=${Math.floor(time)}`;
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link to this moment copied"); setTimeout(() => setCopied(false), 1800); } catch { toast(url); }
  };
  const pct = duration ? Math.min(100, (time / duration) * 100) : 0;
  const onBar = (e) => { const rect = e.currentTarget.getBoundingClientRect(); r.seek(((e.clientX - rect.left) / rect.width) * duration); };

  return (
    <div className={`fixed z-[70] bg-[#120A24] text-white shadow-2xl ring-1 ring-[#54E3FF]/40 ${isMobile ? "inset-x-0 bottom-0 rounded-t-3xl max-h-[62vh]" : "right-4 bottom-4 w-[400px] rounded-3xl max-h-[85vh]"} flex flex-col overflow-hidden`} data-testid="radio-dock">
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <img src={ep.cover_url || STARFLOWER_LOGO} alt="" className="h-12 w-12 rounded-xl object-cover ring-2 ring-[#54E3FF]/60 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black tracking-[0.2em] text-[#54E3FF]">{ep.show || "RANGE FREE RADIO"} · ON AIR</p>
          <Link to={`/news/${ep.slug}`} className="block font-bold text-sm leading-tight truncate text-white no-underline hover:underline" data-testid="radio-dock-title">{ep.title}</Link>
          {chapterIndex >= 0 && <p className="text-xs text-white/60 truncate" data-testid="radio-dock-chapter">{ep.chapters[chapterIndex].title}</p>}
        </div>
        <button type="button" onClick={() => r.setMinimized(true)} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Minimize" data-testid="radio-dock-minimize"><Minus className="h-4 w-4" /></button>
        <button type="button" onClick={r.close} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Close" data-testid="radio-dock-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="px-4">
        <div className="relative h-2.5 rounded-full bg-white/15 cursor-pointer" onClick={onBar} data-testid="radio-dock-bar">
          <div className="absolute inset-y-0 left-0 rounded-full bg-[#54E3FF]" style={{ width: `${pct}%` }} />
          <Ticks episode={ep} duration={duration} onSeek={r.seek} />
        </div>
        <div className="flex justify-between text-[11px] font-mono text-white/60 mt-1"><span data-testid="radio-dock-time">{fmtTime(time)}</span><span>{fmtTime(duration)}</span></div>
      </div>
      <div className="flex items-center justify-center gap-3 px-4 py-2">
        <button type="button" onClick={() => r.skip(-15)} className="p-2 rounded-full hover:bg-white/10" aria-label="Back 15 seconds" data-testid="radio-dock-back"><RotateCcw className="h-5 w-5" /></button>
        <button type="button" onClick={r.toggle} className="h-12 w-12 rounded-full bg-[#54E3FF] text-[#120A24] flex items-center justify-center hover:bg-[#7eeaff]" aria-label={playing ? "Pause" : "Play"} data-testid="radio-dock-play">
          {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
        </button>
        <button type="button" onClick={() => r.skip(15)} className="p-2 rounded-full hover:bg-white/10" aria-label="Forward 15 seconds" data-testid="radio-dock-fwd"><RotateCw className="h-5 w-5" /></button>
        <button type="button" onClick={r.cycleRate} className="ml-2 rounded-full border border-white/25 px-2.5 py-1 text-xs font-bold hover:bg-white/10" data-testid="radio-dock-rate">{rate}×</button>
        <button type="button" onClick={copyMoment} className="rounded-full border border-white/25 px-2.5 py-1 text-xs font-bold hover:bg-white/10 inline-flex items-center gap-1" data-testid="radio-dock-copy">{copied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />} moment</button>
      </div>
      <div className="px-3 pb-3 overflow-y-auto">
        <p className="text-[10px] font-black tracking-[0.2em] text-white/50 px-2 mb-1 flex items-center justify-between gap-2"><span className="inline-flex items-center gap-1"><ListMusic className="h-3 w-3" /> CHAPTERS — tap to jump</span><MarkMoment episode={ep} time={time} /></p>
        <ChapterList episode={ep} current={chapterIndex} onPick={(t) => r.load(ep, t)} compact testId="radio-dock-chapters" />
        <div className="flex items-center justify-between px-2 pt-2 text-[11px] text-white/50">
          <Link to="/news" className="hover:text-white no-underline" data-testid="radio-dock-news-link">All episodes · THE WALL News →</Link>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/radio/feed.xml`} className="inline-flex items-center gap-1 hover:text-white no-underline" data-testid="radio-dock-rss"><Rss className="h-3 w-3" /> RSS</a>
        </div>
      </div>
    </div>
  );
};
